import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { AlertTriangle, CheckCircle, XCircle, Database, Loader, FileText, AlertCircle, Lightbulb } from "lucide-react";

const DataQualityDashboard = () => {
  const [loggedInUser, setLoggedInUser] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch logged-in user
  useEffect(() => {
    try {
      const accountKeys = JSON.parse(sessionStorage.getItem("msal.account.keys"));
      if (accountKeys && accountKeys[0]) {
        const user = JSON.parse(sessionStorage.getItem(accountKeys[0]));
        setLoggedInUser(user.name.split(" "));
      }
    } catch (err) {
      console.warn("Could not retrieve user info from sessionStorage");
      setLoggedInUser(["User"]);
    }
  }, []);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch("https://osapi.bluegecko.ai:8003/dq_summary/", {
          method: "GET",
          headers: {
            "accept": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getInsightIcon = (priority) => {
    switch (priority) {
      case "high":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "medium":
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case "low":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <Database className="h-4 w-4 text-blue-500" />;
    }
  };

  const getQualityColor = (score) => {
    const numScore = typeof score === "string" ? parseFloat(score) : score;
    if (numScore >= 90) return "#22c55e";
    if (numScore >= 70) return "#1D4ED8";
    return "#FC8716";
  };

  const getStatusBgColor = (status) => {
    switch (status?.toLowerCase()) {
      case "good":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-blue-100 text-blue-700";
      case "poor":
        return "bg-[#FC8716]/10 text-[#FC8716]";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center space-y-4">
          <Loader className="h-12 w-12 text-blue-600 animate-spin" />
          <p className="text-lg text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center space-y-4">
          <XCircle className="h-12 w-12 text-red-500" />
          <p className="text-lg text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // No Data State
  if (!dashboardData) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <p className="text-lg text-gray-600">No data available</p>
      </div>
    );
  }

  const parsePercentage = (value) => {
    if (typeof value === "string") {
      // Remove % sign and parse
      return parseFloat(value.replace("%", ""));
    }
    return value;
  };
  
  const qualityScore = parsePercentage(dashboardData.overall_database_quality);
  const qualityColor = qualityScore >= 80 ? "text-green-600" : qualityScore >= 60 ? "text-yellow-600" : "text-red-600";
  
  // Map table_quality_chart to tables for compatibility
  const tables = dashboardData.table_quality_chart || dashboardData.tables || [];

  const getDimensionInsights = (dimensions) => {
  if (!dimensions || dimensions.length === 0) return [];
  
  return dimensions
    .filter(dim => dim.applicable === true)
    .map(dim => ({
      dimension: dim.Dimension,
      successRate: dim.Avg_Success_Rate,
      failCount: dim.Fail,
      successCount: dim.Success,
      priority: dim.Avg_Success_Rate < 50 ? 'high' : dim.Avg_Success_Rate < 75 ? 'medium' : 'low'
    }))
    .sort((a, b) => a.successRate - b.successRate);
};

  const getOverallStatus = (qualityScoreValue) => {
    const score = typeof qualityScoreValue === "string" 
      ? parsePercentage(qualityScoreValue) 
      : qualityScoreValue;
    if (score < 50) return "Low";
    if (score < 75) return "Medium";
    return "High";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "High":
        return "#22c55e";
      case "Medium":
        return "#1D4ED8";
      case "Low":
        return "#FC8716";
      default:
        return "#6B7280";
    }
  };

  // Component for Database Issues Tabs
  const DatabaseIssuesTabs = ({ issuesText }) => {
    const [activeIssueTab, setActiveIssueTab] = useState(0);
    
    // Parse the issues text into sections
    const parseIssuesText = (text) => {
      const sections = [];
      const lines = text.split(/\n/);
      
      let currentSection = null;
      
      lines.forEach((line) => {
        const trimmed = line.trim();
        
        // Check if line starts with a number followed by a dot (e.g., "1.", "2.", "3.")
        const numberedMatch = trimmed.match(/^(\d+)\.\s*(.+)$/);
        
        if (numberedMatch) {
          // Save previous section if exists
          if (currentSection) {
            sections.push(currentSection);
          }
          
          // Start new section
          const [, number, content] = numberedMatch;
          currentSection = {
            number,
            content: content.trim(),
            subPoints: []
          };
        } else if (currentSection) {
          // Add line to current section
          if (trimmed.length > 0) {
            // Check if it's a sub-point
            if (/^\s*[-•]\s/.test(line) || /^[-•]\s/.test(trimmed)) {
              const subContent = trimmed.replace(/^\s*[-•]\s*/, "").trim();
              currentSection.subPoints.push(subContent);
            } else {
              // Append to main content
              currentSection.content += (currentSection.content ? " " : "") + trimmed;
            }
          }
        }
      });
      
      // Add last section
      if (currentSection) {
        sections.push(currentSection);
      }
      
      // Fallback: if no sections found, try splitting by double newlines
      if (sections.length === 0) {
        text.split(/\n\n/).forEach((section) => {
          const trimmed = section.trim();
          const numMatch = trimmed.match(/^(\d+)\.\s*(.+)/s);
          if (numMatch) {
            const [, number, content] = numMatch;
            const contentLines = content.split(/\n/).filter(l => l.trim().length > 0);
            const mainContent = contentLines[0]?.trim() || "";
            const subPoints = contentLines.slice(1).map(l => {
              const trimmed = l.trim();
              return trimmed.replace(/^\s*[-•]\s*/, "").trim();
            }).filter(s => s.length > 0);
            
            sections.push({
              number,
              content: mainContent,
              subPoints
            });
          }
        });
      }
      
      return sections;
    };

    const sections = parseIssuesText(issuesText);
    
    const tabConfig = [
      { id: 0, label: "Summary", icon: FileText },
      { id: 1, label: "Key Issues", icon: AlertCircle },
      { id: 2, label: "Recommendations", icon: Lightbulb },
    ];

    return (
      <div className="space-y-4">
        {/* Horizontal Tabs */}
        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
          <div className="flex gap-1 sm:gap-2 border-b border-gray-200 min-w-max sm:min-w-0">
            {tabConfig.map((tab) => {
              const Icon = tab.icon;
              const section = sections[tab.id];
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveIssueTab(tab.id)}
                  className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 md:px-5 py-2 sm:py-3 font-semibold text-xs sm:text-sm transition-all border-b-2 relative whitespace-nowrap flex-shrink-0 ${
                    activeIssueTab === tab.id
                      ? "text-blue-600 border-blue-600 bg-white"
                      : "text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300"
                  }`}
                  disabled={!section}
                >
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{tab.label}</span>
                  {activeIssueTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-[200px]">
          {sections[activeIssueTab] && (
            <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className={`rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0 ${
                  activeIssueTab === 0 
                    ? "bg-blue-100 text-blue-700" 
                    : activeIssueTab === 1 
                    ? "bg-orange-100 text-orange-700" 
                    : "bg-green-100 text-green-700"
                }`}>
                  {(() => {
                    const Icon = tabConfig[activeIssueTab].icon;
                    return <Icon className="w-4 h-4 sm:w-5 sm:h-5" />;
                  })()}
                </div>
                <div className="flex-1 space-y-2 sm:space-y-3 min-w-0">
                  {/* Main content */}
                  {sections[activeIssueTab].content && (
                    <p className="text-gray-800 leading-relaxed text-xs sm:text-sm font-medium break-words">
                      {sections[activeIssueTab].content}
                    </p>
                  )}
                  
                  {/* Sub-points */}
                  {sections[activeIssueTab].subPoints && sections[activeIssueTab].subPoints.length > 0 && (
                    <div className="space-y-2 mt-3 sm:mt-4">
                      {sections[activeIssueTab].subPoints.map((subPoint, subIndex) => (
                        <div
                          key={subIndex}
                          className="flex items-start gap-2"
                        >
                          <span className={`mt-1 sm:mt-1.5 flex-shrink-0 text-xs font-bold ${
                            activeIssueTab === 0 
                              ? "text-blue-600" 
                              : activeIssueTab === 1 
                              ? "text-orange-600" 
                              : "text-green-600"
                          }`}>•</span>
                          <p className="text-gray-700 leading-relaxed text-xs sm:text-sm break-words">{subPoint}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-800 ml-5 mt-2 via-blue-500 to-orange-500 rounded-3xl p-10 text-white max-w-full mb-4">
        <h1 className="text-4xl font-bold mb-4 w-full">
          Welcome to Owl Sight Workspace, <br />
          {loggedInUser?.[0]}
          {loggedInUser?.length > 1 && " " + loggedInUser[loggedInUser.length - 1]}
          !
        </h1>
        <p className="text-lg">
          BlueGecko Owl Sight is a comprehensive migration data quality
          solution.
          <br />
          With Owl Sight, enterprises gain confidence that every dataset is
          accurate, compliant, and migration-ready
        </p>
      </div>

      {/* Main Content */}
      <div className="flex flex-1">
        <main className="flex-1">
          <div className="p-6">
            <h2 className="text-3xl font-semibold text-blue-900 mb-4">
              Data Quality Overview
            </h2>
            <div className="space-y-6">
   {/* KPI Cards */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">

  {/* Overall Database Quality */}
  <div className="bg-white rounded-xl border border-gray-200 p-8 
                  transition-all duration-300 ease-in-out
                  hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg hover:scale-[1.03] cursor-pointer">
    <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
      Overall Database Quality
    </h2>
    <p
      className="text-5xl font-bold"
      style={{ color: getQualityColor(qualityScore) }}
    >
      {dashboardData.overall_database_quality || `${qualityScore}%`}
    </p>
  </div>

  {/* Total Tables */}
  <div className="bg-white rounded-xl border border-gray-200 p-8 
                  transition-all duration-300 ease-in-out
                  hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg hover:scale-[1.03] cursor-pointer">
    <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
      Total Tables
    </h2>
    <p className="text-5xl font-bold text-blue-600">
      {dashboardData.total_tables}
    </p>
  </div>

  {/* Total Columns */}
  <div className="bg-white rounded-xl border border-gray-200 p-8 
                  transition-all duration-300 ease-in-out
                  hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg hover:scale-[1.03] cursor-pointer">
    <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
      Total Columns
    </h2>
    <p className="text-5xl font-bold text-blue-600">
      {dashboardData.total_columns}
    </p>
  </div>

  {/* Overall Status */}
  <div className="bg-white rounded-xl border border-gray-200 p-8 
                  transition-all duration-300 ease-in-out
                  hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg hover:scale-[1.03] cursor-pointer">
    <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
      Overall Status
    </h2>
    <p
      className="text-5xl font-bold"
      style={{ color: getStatusColor(getOverallStatus(qualityScore)) }}
    >
      {getOverallStatus(qualityScore)}
    </p>
  </div>

</div>




              {/* Table Quality Overview */}
              {tables && Array.isArray(tables) && tables.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-xl font-semibold text-blue-900 mb-4">
                    Table Quality Overview
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                            Table Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            DQ Score (%)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Number of Rules
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Records
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Column Count
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tables.map((table, index) => (
                          <tr key={table.table_name || index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-base font-medium text-gray-900">
                              {table.table_name}
                            </td>
                            <td
                              className="px-6 py-4 whitespace-nowrap text-base font-semibold"
                              style={{
                                color: getQualityColor(table.dq_score),
                              }}
                            >
                              {table.dq_score}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-base text-gray-900">
                              {table.number_of_rules || "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {table.total_records?.toLocaleString() || table.total_records || "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {table.total_columns || "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBgColor(
                                  table.status
                                )}`}
                              >
                                {table.status || "N/A"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Charts and Issues */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Quality Distribution Chart */}
       {dashboardData.overall_quality_distribution && Array.isArray(dashboardData.overall_quality_distribution) && dashboardData.overall_quality_distribution.length > 0 && (
 <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 lg:p-8">
  <div className="mb-4 sm:mb-6">
    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Overall Quality Distribution</h3>
    <p className="text-xs sm:text-sm text-gray-500 mt-1">Quality metrics across all dimensions</p>
  </div>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={dashboardData.overall_quality_distribution
          .filter(dim => dim.applicable === true)
    .map((dim) => ({
  name: dim.Dimension,
  successRate: parseFloat(dim.Avg_Success_Rate?.toFixed(1)) || 0,
  successCount: dim.Success,
  failCount: dim.Fail,
}))

}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="name"
          angle={-45}
          textAnchor="end"
          height={100}
        />
        {/* <YAxis domain={[0, 100]} label={{ value: 'Success Rate (%)', angle: -90, position: 'insideLeft' }} /> */}
      <Tooltip 
  content={({ active, payload }) => {
    if (active && payload?.[0]) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-sm text-gray-900">{data.name}</p>
          <p className="text-xs text-green-600 mt-1">Success: {data.successRate}%</p>
          <p className="text-xs text-gray-600">Passed: {data.successCount?.toLocaleString()}</p>
          <p className="text-xs text-gray-600">Failed: {data.failCount?.toLocaleString()}</p>
        </div>
      );
    }
    return null;
  }}
  contentStyle={{ backgroundColor: "transparent", border: "none" }}
/>
        <Bar dataKey="successRate" fill="#3B82F6" name="Success Rate (%)" />
      </BarChart>
    </ResponsiveContainer>
  </div>
)}

                {/* Database Issues */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4">
                    <Database className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                    <h3 className="text-lg sm:text-xl font-semibold text-blue-900">
                      Database-wide Issues & Insights
                    </h3>
                  </div>
                  
                  {dashboardData.database_wide_issues &&
                    typeof dashboardData.database_wide_issues === "string" && (
                      <DatabaseIssuesTabs issuesText={dashboardData.database_wide_issues} />
                    )}
                  
                  {dashboardData.ai_generated_insights &&
                    Array.isArray(dashboardData.ai_generated_insights) &&
                    dashboardData.ai_generated_insights.length > 0 && (
                      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                        <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">AI Generated Insights</h4>
                        <div className="space-y-2 sm:space-y-3">
                          {dashboardData.ai_generated_insights.map((insight, index) => (
                            <div
                              key={index}
                              className="flex items-start p-2 sm:p-3 rounded-lg bg-gray-50 border border-gray-100"
                            >
                              <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                                {getInsightIcon(insight.priority)}
                              </div>
                              <div className="ml-2 sm:ml-3 flex-1 min-w-0">
                                <p className="font-medium text-xs sm:text-sm text-gray-900 break-words">
                                  {insight.title}
                                </p>
                                <p className="text-xs text-gray-600 mt-1 break-words">
                                  {insight.description}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DataQualityDashboard;